// ============================================================================
// TRAINING DECISION LOGGER
// ============================================================================
// Tracks every decision made by the player during training sessions.
// This data is used by the training analysis service to evaluate play quality.
// ============================================================================

import { Card, Player, GameState } from '../../types/GameState';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type Position = 'UTG' | 'UTG+1' | 'MP' | 'MP+1' | 'CO' | 'BTN' | 'SB' | 'BB';
export type PositionCategory = 'EP' | 'MP' | 'LP' | 'BLINDS';
export type Street = 'preflop' | 'flop' | 'turn' | 'river';
export type ActionType = 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'all-in';
export type BoardTexture = 'dry' | 'wet' | 'semi-wet';
export type HandStrength = 'monster' | 'strong' | 'medium' | 'weak' | 'draw' | 'nothing';
export type DrawType = 'flush-draw' | 'oesd' | 'gutshot' | 'combo-draw' | 'backdoor' | 'none';

export interface LoggedDecision {
  id: string;
  handNumber: number;
  timestamp: number;
  
  // Position info
  position: Position;
  positionCategory: PositionCategory;
  playersAtTable: number;
  playersInHand: number;
  
  // Street info
  street: Street;
  isFirstToAct: boolean;
  facingBet: boolean;
  facingRaise: boolean;
  
  // Cards
  holeCards: Card[];
  communityCards: Card[];
  boardTexture: BoardTexture;
  
  // Hand evaluation
  handStrength: HandStrength;
  handDescription: string;
  drawType: DrawType;
  outs: number;
  equity: number; // Estimated equity percentage
  
  // Action taken
  action: ActionType;
  betAmount: number;
  potSizeBeforeAction: number;
  potOdds: number; // Ratio offered (e.g., 3 for 3:1)
  requiredEquity: number; // Minimum equity needed to call
  
  // Context
  wasPreflopRaiser: boolean;
  wasAggressor: boolean;
  opponentCount: number;
  stackSize: number;
  effectiveStack: number;
  potToStackRatio: number;
  // Big blind at the time of the decision (challenge blinds escalate) -
  // lets the coach compute exact stack depth in bb per hand.
  bigBlind?: number;
  
  // Bet sizing
  betSizeRelativeToPot: number; // e.g., 0.5 for half pot
  
  // Outcome (filled after hand completes)
  handWon?: boolean;
  showdownReached?: boolean;
  profitLoss?: number;
}

// The challenge this session was played under (when launched from
// Tournament Training) - lets the report and AI coach evaluate hands
// against the challenge's winning approach instead of generic play.
export interface SessionChallengeContext {
  id: string;
  title: string;
  mission?: string;
  tips?: string[];
  skill?: string;
  concept?: string;
  goalType?: string;
  goalChips?: number;
  heroChips?: number;
  maxHands?: number;
}

export interface TrainingSession {
  sessionId: string;
  uid: string;
  username: string;
  tableId: string;
  tableName: string;

  // Session config
  botDifficulty: 'easy' | 'medium' | 'hard' | 'mixed';
  botCount: number;
  humanCount: number;
  blinds: { small: number; big: number };
  startingStack: number;

  // Challenge context (challenge tables only)
  challenge?: SessionChallengeContext;
  // Set by the game page when the run settles - the report and coach know
  // whether the challenge was WON, not just how it was played.
  challengeOutcome?: { outcome: 'won' | 'lost'; heroChips: number; handsPlayed: number };
  
  // Timing
  startTime: number;
  endTime?: number;
  duration?: number;
  
  // Decisions
  decisions: LoggedDecision[];
  handsPlayed: number;
  
  // Quick stats for filtering
  vpipCount: number;
  pfrCount: number;
  totalOpportunities: number;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get position category from specific position
 */
export function getPositionCategory(position: Position): PositionCategory {
  switch (position) {
    case 'UTG':
    case 'UTG+1':
      return 'EP';
    case 'MP':
    case 'MP+1':
      return 'MP';
    case 'CO':
    case 'BTN':
      return 'LP';
    case 'SB':
    case 'BB':
      return 'BLINDS';
    default:
      return 'MP';
  }
}

/**
 * Determine position based on seat index and dealer position
 */
export function determinePosition(
  seatIndex: number,
  dealerIndex: number,
  totalPlayers: number
): Position {
  // Calculate position relative to dealer
  const relativePos = (seatIndex - dealerIndex + totalPlayers) % totalPlayers;
  
  if (totalPlayers <= 3) {
    // 3 or fewer players: simplified positions
    if (relativePos === 0) return 'BTN';
    if (relativePos === 1) return 'SB';
    return 'BB';
  }
  
  if (totalPlayers <= 6) {
    // 6-max positions
    const positions: Position[] = ['BTN', 'SB', 'BB', 'UTG', 'MP', 'CO'];
    return positions[relativePos] || 'MP';
  }
  
  // Full ring (8 players)
  const positions: Position[] = ['BTN', 'SB', 'BB', 'UTG', 'UTG+1', 'MP', 'MP+1', 'CO'];
  return positions[relativePos] || 'MP';
}

/**
 * Analyze board texture
 */
export function analyzeBoardTexture(communityCards: Card[]): BoardTexture {
  if (communityCards.length === 0) return 'dry';
  
  // Count suits
  const suitCounts: Record<string, number> = {};
  communityCards.forEach(card => {
    suitCounts[card.suit] = (suitCounts[card.suit] || 0) + 1;
  });
  const maxSuitCount = Math.max(...Object.values(suitCounts));
  
  // Check for connectedness
  const ranks = communityCards.map(card => cardRankToNumber(card.rank)).sort((a, b) => a - b);
  let connectedness = 0;
  for (let i = 1; i < ranks.length; i++) {
    const gap = ranks[i] - ranks[i - 1];
    if (gap <= 2) connectedness++;
  }
  
  // Check for pairs on board
  const rankCounts: Record<string, number> = {};
  communityCards.forEach(card => {
    rankCounts[card.rank] = (rankCounts[card.rank] || 0) + 1;
  });
  const hasPair = Object.values(rankCounts).some(count => count >= 2);
  
  // Wet board: flush draw possible (3+ same suit) or straight draw possible (connected)
  if (maxSuitCount >= 3) return 'wet';
  if (connectedness >= 2 && !hasPair) return 'wet';
  if (maxSuitCount === 2 && connectedness >= 1) return 'semi-wet';
  
  return 'dry';
}

/**
 * Convert card rank to number for comparison
 */
function cardRankToNumber(rank: string): number {
  const rankMap: Record<string, number> = {
    '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8,
    '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14
  };
  return rankMap[rank] || 0;
}

/**
 * Calculate pot odds
 */
export function calculatePotOdds(potSize: number, callAmount: number): number {
  if (callAmount <= 0) return Infinity;
  return (potSize + callAmount) / callAmount;
}

/**
 * Calculate required equity to call
 */
export function calculateRequiredEquity(potSize: number, callAmount: number): number {
  if (potSize + callAmount <= 0) return 0;
  return callAmount / (potSize + callAmount) * 100;
}

/**
 * Estimate equity based on draw type and street
 */
export function estimateEquity(
  drawType: DrawType,
  handStrength: HandStrength,
  street: Street
): number {
  // Base equity from hand strength
  let equity = 0;
  
  switch (handStrength) {
    case 'monster': equity = 85; break;
    case 'strong': equity = 70; break;
    case 'medium': equity = 50; break;
    case 'weak': equity = 30; break;
    case 'draw': equity = 0; break; // Will be calculated from draw
    case 'nothing': equity = 10; break;
  }
  
  // Add draw equity
  if (drawType !== 'none') {
    const outs = getOutsForDraw(drawType);
    if (street === 'flop') {
      // Rule of 4 for flop (two cards to come)
      equity = Math.max(equity, Math.min(outs * 4, 70));
    } else if (street === 'turn') {
      // Rule of 2 for turn (one card to come)
      equity = Math.max(equity, Math.min(outs * 2, 50));
    }
  }
  
  return equity;
}

/**
 * Get number of outs for a draw type
 */
export function getOutsForDraw(drawType: DrawType): number {
  switch (drawType) {
    case 'flush-draw': return 9;
    case 'oesd': return 8;
    case 'gutshot': return 4;
    case 'combo-draw': return 15; // Flush + straight
    case 'backdoor': return 3;
    case 'none': return 0;
  }
}

/**
 * Evaluate hand strength (simplified)
 */
export function evaluateHandStrength(
  holeCards: Card[],
  communityCards: Card[]
): { strength: HandStrength; description: string } {
  if (communityCards.length === 0) {
    // Preflop hand strength
    return evaluatePreflopStrength(holeCards);
  }
  
  // Post-flop hand strength (simplified)
  return evaluatePostflopStrength(holeCards, communityCards);
}

/**
 * Evaluate preflop hand strength
 */
function evaluatePreflopStrength(holeCards: Card[]): { strength: HandStrength; description: string } {
  if (holeCards.length !== 2) {
    return { strength: 'nothing', description: 'Unknown' };
  }
  
  const [card1, card2] = holeCards;
  const rank1 = cardRankToNumber(card1.rank);
  const rank2 = cardRankToNumber(card2.rank);
  const suited = card1.suit === card2.suit;
  const paired = rank1 === rank2;
  const highCard = Math.max(rank1, rank2);
  const lowCard = Math.min(rank1, rank2);
  const gap = highCard - lowCard;
  const connected = gap === 1;
  
  // Premium pairs
  if (paired) {
    if (highCard >= 13) return { strength: 'monster', description: `Pocket ${card1.rank}s` };
    if (highCard >= 10) return { strength: 'strong', description: `Pocket ${card1.rank}s` };
    if (highCard >= 7) return { strength: 'medium', description: `Pocket ${card1.rank}s` };
    return { strength: 'weak', description: `Pocket ${card1.rank}s` };
  }
  
  // Big cards
  if (highCard === 14) { // Ace
    if (lowCard >= 13) return { strength: 'monster', description: suited ? 'AKs' : 'AKo' };
    if (lowCard >= 12) return { strength: 'strong', description: suited ? `A${card2.rank}s` : `A${card2.rank}o` };
    if (lowCard >= 10 && suited) return { strength: 'medium', description: `A${card2.rank}s` };
    if (suited) return { strength: 'draw', description: `A${card2.rank}s (suited)` };
    return { strength: 'weak', description: `A${card2.rank}o` };
  }
  
  // Broadway cards
  if (highCard >= 11 && lowCard >= 10) {
    if (suited) return { strength: 'medium', description: `${card1.rank}${card2.rank}s` };
    return { strength: 'weak', description: `${card1.rank}${card2.rank}o` };
  }
  
  // Suited connectors
  if (suited && connected && lowCard >= 5) {
    return { strength: 'draw', description: `${card1.rank}${card2.rank}s (suited connector)` };
  }
  
  // Suited one-gappers
  if (suited && gap === 2 && lowCard >= 5) {
    return { strength: 'draw', description: `${card1.rank}${card2.rank}s (suited one-gapper)` };
  }
  
  return { strength: 'nothing', description: `${card1.rank}${card2.rank}${suited ? 's' : 'o'}` };
}

/**
 * Evaluate postflop hand strength (simplified - would need full hand evaluator for accuracy)
 */
function evaluatePostflopStrength(
  holeCards: Card[],
  communityCards: Card[]
): { strength: HandStrength; description: string } {
  // This is a simplified evaluation
  // In production, you'd use your existing handEvaluationService
  
  const allCards = [...holeCards, ...communityCards];
  
  // Check for pairs, trips, etc.
  const rankCounts: Record<string, number> = {};
  allCards.forEach(card => {
    rankCounts[card.rank] = (rankCounts[card.rank] || 0) + 1;
  });
  
  const counts = Object.values(rankCounts).sort((a, b) => b - a);
  const maxCount = counts[0];
  
  // Check for flush
  const suitCounts: Record<string, number> = {};
  allCards.forEach(card => {
    suitCounts[card.suit] = (suitCounts[card.suit] || 0) + 1;
  });
  const hasFlush = Object.values(suitCounts).some(count => count >= 5);
  
  if (hasFlush) return { strength: 'monster', description: 'Flush' };
  if (maxCount >= 4) return { strength: 'monster', description: 'Four of a Kind' };
  if (maxCount === 3 && counts[1] >= 2) return { strength: 'monster', description: 'Full House' };
  if (maxCount === 3) return { strength: 'strong', description: 'Three of a Kind' };
  if (maxCount === 2 && counts[1] === 2) return { strength: 'medium', description: 'Two Pair' };
  if (maxCount === 2) {
    // Check if it's top pair, middle pair, etc.
    const pairRank = Object.entries(rankCounts).find(([_, count]) => count === 2)?.[0];
    const boardRanks = communityCards.map(c => cardRankToNumber(c.rank)).sort((a, b) => b - a);
    const pairRankNum = cardRankToNumber(pairRank || '2');
    
    if (pairRankNum >= boardRanks[0]) return { strength: 'medium', description: 'Top Pair' };
    if (pairRankNum >= boardRanks[1]) return { strength: 'weak', description: 'Middle Pair' };
    return { strength: 'weak', description: 'Bottom Pair' };
  }
  
  // High card
  const highCard = Math.max(...holeCards.map(c => cardRankToNumber(c.rank)));
  if (highCard >= 14) return { strength: 'weak', description: 'Ace High' };
  
  return { strength: 'nothing', description: 'High Card' };
}

/**
 * Detect draw type
 */
export function detectDrawType(holeCards: Card[], communityCards: Card[]): DrawType {
  if (communityCards.length === 0) return 'none';
  
  const allCards = [...holeCards, ...communityCards];
  
  // Check for flush draw
  const suitCounts: Record<string, number> = {};
  allCards.forEach(card => {
    suitCounts[card.suit] = (suitCounts[card.suit] || 0) + 1;
  });
  const hasFlushDraw = Object.values(suitCounts).some(count => count === 4);
  
  // Check for straight draw
  const ranks = [...new Set(allCards.map(card => cardRankToNumber(card.rank)))].sort((a, b) => a - b);
  
  // Check for OESD (open-ended straight draw)
  let hasOESD = false;
  let hasGutshot = false;
  
  for (let i = 0; i <= ranks.length - 4; i++) {
    const slice = ranks.slice(i, i + 4);
    const gaps = [];
    for (let j = 1; j < slice.length; j++) {
      gaps.push(slice[j] - slice[j - 1]);
    }
    const totalGap = slice[3] - slice[0];
    
    if (totalGap === 3) {
      hasOESD = true;
    } else if (totalGap === 4 && gaps.filter(g => g === 1).length === 2) {
      hasGutshot = true;
    }
  }
  
  // Combo draw
  if (hasFlushDraw && (hasOESD || hasGutshot)) return 'combo-draw';
  if (hasFlushDraw) return 'flush-draw';
  if (hasOESD) return 'oesd';
  if (hasGutshot) return 'gutshot';
  
  // Backdoor draws (on flop only)
  if (communityCards.length === 3) {
    const maxSuitCount = Math.max(...Object.values(suitCounts));
    if (maxSuitCount === 3) return 'backdoor';
  }
  
  return 'none';
}

// ============================================================================
// TRAINING DECISION LOGGER CLASS
// ============================================================================

class TrainingDecisionLogger {
  private currentSession: TrainingSession | null = null;
  private decisionCounter: number = 0;

  /**
   * Start a new training session
   */
  startSession(config: {
    uid: string;
    username: string;
    tableId: string;
    tableName: string;
    botDifficulty: 'easy' | 'medium' | 'hard' | 'mixed';
    botCount: number;
    humanCount: number;
    blinds: { small: number; big: number };
    startingStack: number;
    challenge?: SessionChallengeContext;
  }): TrainingSession {
    this.decisionCounter = 0;
    
    this.currentSession = {
      sessionId: `training_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...config,
      startTime: Date.now(),
      decisions: [],
      handsPlayed: 0,
      vpipCount: 0,
      pfrCount: 0,
      totalOpportunities: 0,
    };
    
    console.log(`📝 [TrainingLogger] Started session ${this.currentSession.sessionId}`);
    return this.currentSession;
  }

  /**
   * Log a player decision
   */
  logDecision(params: {
    handNumber: number;
    player: Player;
    seatIndex: number;
    dealerIndex: number;
    totalPlayers: number;
    street: Street;
    action: ActionType;
    betAmount: number;
    potSize: number;
    callAmount: number;
    communityCards: Card[];
    wasPreflopRaiser: boolean;
    wasAggressor: boolean;
    playersInHand: number;
    effectiveStack: number;
    bigBlind?: number;
  }): LoggedDecision | null {
    if (!this.currentSession) {
      console.warn('⚠️ [TrainingLogger] No active session');
      return null;
    }
    
    const holeCards = params.player.holeCards || [];
    const position = determinePosition(params.seatIndex, params.dealerIndex, params.totalPlayers);
    const positionCategory = getPositionCategory(position);
    const boardTexture = analyzeBoardTexture(params.communityCards);
    const { strength: handStrength, description: handDescription } = evaluateHandStrength(holeCards, params.communityCards);
    const drawType = detectDrawType(holeCards, params.communityCards);
    const outs = getOutsForDraw(drawType);
    const equity = estimateEquity(drawType, handStrength, params.street);
    const potOdds = calculatePotOdds(params.potSize, params.callAmount);
    const requiredEquity = calculateRequiredEquity(params.potSize, params.callAmount);
    
    const decision: LoggedDecision = {
      id: `decision_${++this.decisionCounter}`,
      handNumber: params.handNumber,
      timestamp: Date.now(),
      
      position,
      positionCategory,
      playersAtTable: params.totalPlayers,
      playersInHand: params.playersInHand,
      
      street: params.street,
      isFirstToAct: params.callAmount === 0 && !params.wasPreflopRaiser,
      facingBet: params.callAmount > 0,
      facingRaise: params.wasAggressor === false && params.callAmount > 0,
      
      holeCards,
      communityCards: [...params.communityCards],
      boardTexture,
      
      handStrength,
      handDescription,
      drawType,
      outs,
      equity,
      
      action: params.action,
      betAmount: params.betAmount,
      potSizeBeforeAction: params.potSize,
      potOdds,
      requiredEquity,
      
      wasPreflopRaiser: params.wasPreflopRaiser,
      wasAggressor: params.wasAggressor,
      opponentCount: params.playersInHand - 1,
      stackSize: params.player.chips,
      effectiveStack: params.effectiveStack,
      potToStackRatio: params.potSize / params.effectiveStack,
      
      betSizeRelativeToPot: params.potSize > 0 ? params.betAmount / params.potSize : 0,
      ...(params.bigBlind ? { bigBlind: params.bigBlind } : {}),
    };
    
    this.currentSession.decisions.push(decision);
    
    // Update quick stats
    if (params.street === 'preflop' && (params.action === 'call' || params.action === 'bet' || params.action === 'raise')) {
      this.currentSession.vpipCount++;
      if (params.action === 'raise' || params.action === 'bet') {
        this.currentSession.pfrCount++;
      }
    }
    if (params.street === 'preflop') {
      this.currentSession.totalOpportunities++;
    }
    
    console.log(`📝 [TrainingLogger] Logged: ${params.action} on ${params.street} in ${position}`);
    return decision;
  }

  /**
   * Update hand outcome after hand completes
   */
  setChallengeOutcome(outcome: 'won' | 'lost', heroChips: number, handsPlayed: number): void {
    if (this.currentSession) {
      this.currentSession.challengeOutcome = { outcome, heroChips, handsPlayed };
      console.log(`🏁 [TrainingLogger] Challenge outcome recorded: ${outcome}`);
    }
  }

  updateHandOutcome(handNumber: number, won: boolean, showdown: boolean, profitLoss: number): void {
    if (!this.currentSession) return;
    
    const handDecisions = this.currentSession.decisions.filter(d => d.handNumber === handNumber);
    handDecisions.forEach(d => {
      d.handWon = won;
      d.showdownReached = showdown;
      d.profitLoss = profitLoss;
    });
    
    this.currentSession.handsPlayed++;
  }

  /**
   * End the current session
   */
  endSession(): TrainingSession | null {
    if (!this.currentSession) return null;
    
    this.currentSession.endTime = Date.now();
    this.currentSession.duration = this.currentSession.endTime - this.currentSession.startTime;
    
    console.log(`📝 [TrainingLogger] Ended session: ${this.currentSession.handsPlayed} hands, ${this.currentSession.decisions.length} decisions`);
    
    const session = this.currentSession;
    this.currentSession = null;
    return session;
  }

  /**
   * Get current session
   */
  getCurrentSession(): TrainingSession | null {
    return this.currentSession;
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export const trainingDecisionLogger = new TrainingDecisionLogger();
export default trainingDecisionLogger;